import {
  Filter,
  type FilterKey,
  type FilterQuery,
  type FilterRef,
  type SuppliedFilterProvider,
  ValidationMessage,
} from '@github-ui/filter'
import {BlockType} from '@github-ui/filter-core'
import {CreatedFilterProvider} from '@github-ui/filter-core/providers'
import {useDebounce} from '@github-ui/use-debounce'
import {GitBranchIcon, PersonIcon, PulseIcon, WorkflowIcon} from '@primer/octicons-react'
import {useCallback, useId, useMemo, useState} from 'react'
import type React from 'react'

import styles from './WorkflowRunsFilter.module.css'
import {
  ActorSuggestionsFilterProvider,
  BranchSuggestionsFilterProvider,
  EventSuggestionsFilterProvider,
  StatusSuggestionsFilterProvider,
  WorkflowSuggestionsFilterProvider,
} from './workflow-runs-filter-providers'

export type WorkflowRunsFilterKey = 'workflow' | 'event' | 'is' | 'branch' | 'actor'

export interface WorkflowRunsFilterProps {
  owner: string
  repo: string
  filterValue: string
  onChange: (value: string) => void
  onSubmit: NonNullable<React.ComponentProps<typeof Filter>['onSubmit']>
  /** Imperative ref for setting the filter's value from outside the component. */
  ref?: React.Ref<FilterRef>
  'aria-describedby'?: string
  selectedWorkflow: boolean
}

export const filterKeys: Record<WorkflowRunsFilterKey, FilterKey> = {
  workflow: {
    key: 'workflow',
    displayName: 'Workflow',
    description: 'Filter by workflow',
    priority: 1,
    icon: WorkflowIcon,
  },
  event: {
    key: 'event',
    displayName: 'Event',
    description: 'Filter by the event that triggered a workflow run',
    priority: 2,
    icon: WorkflowIcon,
  },
  is: {
    key: 'is',
    aliases: ['status'],
    displayName: 'Status',
    description: 'Filter by workflow run status',
    priority: 3,
    icon: PulseIcon,
  },
  branch: {
    key: 'branch',
    displayName: 'Branch',
    description: 'Filter by the workflow run branch',
    priority: 4,
    icon: GitBranchIcon,
  },
  actor: {
    key: 'actor',
    displayName: 'Actor',
    description: 'Filter by the actor who triggered a workflow run',
    priority: 5,
    icon: PersonIcon,
  },
}

const singleValueOptions = {
  filterTypes: {valueless: false, hasValue: false, multiValue: false},
} as const

export function repoActionsPath(owner: string, repo: string, action: string) {
  return `/${encodeURIComponent(owner)}/${encodeURIComponent(repo)}/actions/${action}`
}

function buildProviders(owner: string, repo: string, selectedWorkflow: boolean): SuppliedFilterProvider[] {
  const providers: SuppliedFilterProvider[] = [
    new EventSuggestionsFilterProvider(filterKeys.event, repoActionsPath(owner, repo, 'events'), singleValueOptions),
    new StatusSuggestionsFilterProvider(filterKeys.is, repoActionsPath(owner, repo, 'statuses'), singleValueOptions),
    new BranchSuggestionsFilterProvider(filterKeys.branch, repoActionsPath(owner, repo, 'branches'), {
      ...singleValueOptions,
      caseSensitiveValues: true,
    }),
    new ActorSuggestionsFilterProvider(filterKeys.actor, repoActionsPath(owner, repo, 'actors'), singleValueOptions),
    new CreatedFilterProvider({
      priority: 6,
      filterTypes: {valueless: false, hasValue: false, multiValue: false},
    }),
  ]

  if (!selectedWorkflow) {
    providers.unshift(
      new WorkflowSuggestionsFilterProvider(
        filterKeys.workflow,
        repoActionsPath(owner, repo, 'workflows'),
        singleValueOptions,
      ),
    )
  }

  return providers
}

const VALIDATION_MESSAGE_DEBOUNCE_MS = 300

// Ignored-term messages echo back raw, user-entered search text, so they
// must never reach analytics at all (not even in a redacted form).
const IGNORED_TERM_SUFFIX = 'will be ignored since log searching is not yet available'
const sanitizeMessage = (message: string) => (message.includes(IGNORED_TERM_SUFFIX) ? '' : message)

export function WorkflowRunsFilter({
  owner,
  repo,
  filterValue,
  onChange,
  onSubmit,
  selectedWorkflow,
  ref,
  'aria-describedby': ariaDescribedBy,
}: WorkflowRunsFilterProps) {
  const generatedId = useId()
  const providers = useMemo(() => buildProviders(owner, repo, selectedWorkflow), [owner, repo, selectedWorkflow])
  const [validationMessages, setValidationMessages] = useState<string[]>([])
  const validationMessageId = `${generatedId}-validation-message`
  const describedBy = [ariaDescribedBy, validationMessages.length > 0 ? validationMessageId : undefined]
    .filter(Boolean)
    .join(' ')

  // Showing the warning is debounced so it doesn't flash while the user is still
  // typing, but clearing it happens immediately so a corrected filter stops warning
  // as soon as possible.
  const showValidationMessages = useDebounce(setValidationMessages, VALIDATION_MESSAGE_DEBOUNCE_MS)

  const handleValidation = useCallback(
    (messages: string[], query: FilterQuery) => {
      const ignoredTerms = new Set(
        query.blocks.filter(block => block.type === BlockType.Text).map(block => `${block.raw} ${IGNORED_TERM_SUFFIX}`),
      )
      const nextMessages = [...messages, ...ignoredTerms]

      if (nextMessages.length === 0) {
        showValidationMessages.cancel()
        setValidationMessages([])
      } else {
        showValidationMessages(nextMessages)
      }
    },
    [showValidationMessages],
  )

  const handleSubmit = useCallback<NonNullable<React.ComponentProps<typeof Filter>['onSubmit']>>(
    (query, eventType) => {
      // Flush any pending warning immediately so it's visible as soon as the user submits.
      showValidationMessages.flush()
      onSubmit(query, eventType)
    },
    [onSubmit, showValidationMessages],
  )

  return (
    <>
      <Filter
        id={generatedId}
        label="Filter workflow runs"
        placeholder="Search runs"
        variant="input"
        providers={providers}
        filterValue={filterValue}
        onChange={onChange}
        onSubmit={handleSubmit}
        onValidation={handleValidation}
        showValidationMessage={false}
        settings={{groupAndKeywordSupport: false}}
        ref={ref}
        aria-describedby={describedBy || undefined}
      />
      <ValidationMessage
        messages={validationMessages}
        id={validationMessageId}
        sanitizeMessage={sanitizeMessage}
        className={styles.ValidationMessage}
      />
    </>
  )
}
